// Per-trade landing pages, e.g. /for/plumbers, /for/hair-stylists.
// Content lives in @/lib/for-trades.ts -- add a TradeContent entry to
// spin up a new page without touching this file.
//
// Page structure:
//   1. Hero (2-col at lg+): copy left, mock app screen right, decorative
//      gradient blur behind. Trade-accent-coloured.
//   2. StatsBand (4 stats, trade-flavoured if defined).
//   3. Sound familiar? -- big hero pain callout (problem + solution)
//      + 3 supporting pain points in a 3-col grid below.
//   4. Benefits (2-col card grid with accent-coloured check icon).
//   5. Workflow (numbered steps, accent-coloured numerals).
//   6. TapToPayCallout (image LEFT so the page reads right-image-left-
//      image-right -- alternating reads better visually than three
//      image-right sections in a row).
//   7. HouseProud sub-brand hero (property trades only) OR
//      BookingPageHero (non-property trades).
//   8. FAQs (if any).
//   9. Final CTA + cross-trade links.
//
// Each section wrapped in FadeInOnScroll. Voice + copy density rules:
// see docs/voice-and-tone.md.
import { Link, useLoaderData } from "react-router";
import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import {
  findTrade,
  isPropertyTrade,
  resolveLocalized,
  resolvePainHero,
  resolveTicketAmount,
  resolveTradeDisplayName,
  TRADES,
  type TradeContent,
  type TradeRegion,
} from "@/lib/for-trades";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  CheckCircle,
  Question,
  Quotes,
  // Per-trade hero-backdrop icons. Reuse the Phosphor vocabulary
  // from _app.onboarding.tsx's TRADE_TYPES list so the marketing
  // pages match what the trader saw on sign-up. Gas / HVAC / tutors
  // aren't in that list yet -- adding sensible analogues here.
  Wrench,
  Lightning,
  HardHat,
  Flame,
  Thermometer,
  Broom,
  Tree,
  PaintBrush,
  LockKey,
  Scissors,
  Dog,
  Books,
  Briefcase,
  Storefront,
  Car,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { HouseProudHero } from "@/components/marketing/HouseProudHero";
import { MockAppScreen } from "@/components/marketing/MockAppScreen";
import { StatsBand } from "@/components/marketing/StatsBand";
import { GradientHeroBackdrop } from "@/components/marketing/GradientHeroBackdrop";
import { TapToPayCallout } from "@/components/marketing/TapToPayCallout";
import { CashCallout } from "@/components/marketing/CashCallout";
import { BookingPageHero } from "@/components/marketing/BookingPageHero";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";
import { getVisitorGeo, getFormatsForCountry } from "@barrykilby/geo";
import { ALLOW_AS_COUNTRIES } from "../root";
import { formatCurrencyForProvider } from "@/lib/format";
import { promoMonthlyDisplay } from "@/lib/plans";

import { useReportRouteError } from "@/lib/useReportRouteError";
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: "Manos" }];
  return [
    { title: `Manos for ${data.displayPlural.toLowerCase()}` },
    { name: "description", content: data.heroSub ?? "" },
  ];
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const trade = findTrade(params.trade ?? "");
  if (!trade) {
    throw new Response("Not found", { status: 404 });
  }
  const geo = getVisitorGeo(request, { allowAsOverride: ALLOW_AS_COUNTRIES });
  // Bucket the visitor for the region-keyed copy. ISO alpha-2 codes
  // for the launch markets so the lookup matches `profile.country`
  // and `visitorGeo.country` shapes used elsewhere; EU + ROW are
  // coarse fallbacks (the resolver maps them to GB copy).
  const region: TradeRegion =
    geo.country === "US"
      ? "US"
      : geo.country === "CA"
        ? "CA"
        : geo.country === "GB"
          ? "GB"
          : geo.isEU
            ? "EU"
            : "ROW";

  // Resolve the tap-to-pay ticket display in the visitor's currency.
  // The numeric ticket amount is now per-region so a US plumber sees
  // a US-realistic ticket size, not the GB number FX-converted.
  // Pull currency + locale from the canonical geo lib map. Single
  // source of truth across Manos -- bootstraps in _app.tsx and
  // profile.update.country-cascade use the same.
  const formats = getFormatsForCountry(geo.country ?? "GB");
  const ticketAmount = resolveTicketAmount(trade, region);
  const tapToPayTicketDisplay =
    ticketAmount !== null
      ? formatCurrencyForProvider(ticketAmount, {
          locale: formats.dateLocale,
          currency: formats.currencyCode,
        })
      : trade.tapToPayTicket ?? null;

  // Resolve the localised text fields server-side so the component
  // receives ready-to-render strings/arrays without having to know
  // about the region-keyed storage shape. Each falls back through
  // the resolver chain (region -> GB -> any) so a Berlin visitor on
  // a doc-uncovered trade still gets the GB copy.
  const heroHeadline = resolveLocalized(trade.heroHeadline, region);
  const heroSub = resolveLocalized(trade.heroSub, region);
  const painPoints = resolveLocalized(trade.painPoints, region) ?? [];
  const benefits = resolveLocalized(trade.benefits, region) ?? [];
  const workflow = resolveLocalized(trade.workflow, region);
  const mockAppScreen = resolveLocalized(trade.mockAppScreen, region);
  const faqs = resolveLocalized(trade.faqs, region);
  const displayName = resolveTradeDisplayName(trade, region);
  // Reasonable plural fallback when no displayName override exists:
  // the route currently treats `plural` as the always-shown form
  // ("Manos for plumbers"). When displayName overrides `name` (gas-
  // engineers, painters), assume the override pluralises by adding
  // an "s" -- correct for "Furnace tech" -> "Furnace techs" and
  // "Painter" -> "Painters". Falls back to the existing trade.plural
  // when no override is set.
  const displayPlural =
    displayName === trade.name
      ? trade.plural
      : displayName.endsWith("s")
        ? displayName
        : `${displayName}s`;

  // Cross-trade chip list at the bottom of the page. Localise the
  // trade names too so a US visitor doesn't see UK chips like
  // "Painters & decorators" alongside their US-flavoured page.
  const otherTrades = TRADES.filter((t) => t.slug !== trade.slug).map((t) => {
    const otherName = resolveTradeDisplayName(t, region);
    const otherPlural =
      otherName === t.name
        ? t.plural
        : otherName.endsWith("s")
          ? otherName
          : `${otherName}s`;
    return { slug: t.slug, plural: otherPlural };
  });

  return {
    trade,
    isProperty: isPropertyTrade(trade.slug),
    promoPrice: promoMonthlyDisplay(geo.country),
    painHero: resolvePainHero(trade, region),
    tapToPayTicketDisplay,
    heroHeadline,
    heroSub,
    painPoints,
    benefits,
    workflow,
    mockAppScreen,
    faqs,
    displayName,
    displayPlural,
    otherTrades,
  };
};

// Fallback stats band -- the same four stats the homepage uses.
const DEFAULT_STATS = [
  { value: "2 sec", label: "to capture a job" },
  { value: "30 sec", label: "to send an invoice" },
  { value: "3 days", label: "free to try" },
  { value: "Any trade", label: "covered" },
];

const ACCENT_CHECK: Record<TradeContent["accentColour"], string> = {
  amber: "text-amber-600",
  blue: "text-blue-600",
  green: "text-green-600",
  rose: "text-rose-600",
  violet: "text-violet-600",
  teal: "text-teal-600",
  orange: "text-orange-600",
};

const ACCENT_NUMERAL_BG: Record<TradeContent["accentColour"], string> = {
  amber: "bg-amber-600",
  blue: "bg-blue-600",
  green: "bg-green-600",
  rose: "bg-rose-600",
  violet: "bg-violet-600",
  teal: "bg-teal-600",
  orange: "bg-orange-600",
};

const ACCENT_HEADLINE: Record<TradeContent["accentColour"], string> = {
  amber: "text-amber-600 dark:text-amber-400",
  blue: "text-blue-600 dark:text-blue-400",
  green: "text-green-600 dark:text-green-400",
  rose: "text-rose-600 dark:text-rose-400",
  violet: "text-violet-600 dark:text-violet-400",
  teal: "text-teal-600 dark:text-teal-400",
  orange: "text-orange-600 dark:text-orange-400",
};

// Pain-hero accent -- quote-mark + solution check pickup the trade
// colour so the callout breathes in the same family as the page.
const ACCENT_PAIN: Record<TradeContent["accentColour"], string> = {
  amber: "text-amber-600 dark:text-amber-400",
  blue: "text-blue-600 dark:text-blue-400",
  green: "text-green-600 dark:text-green-400",
  rose: "text-rose-600 dark:text-rose-400",
  violet: "text-violet-600 dark:text-violet-400",
  teal: "text-teal-600 dark:text-teal-400",
  orange: "text-orange-600 dark:text-orange-400",
};

// Pain-bullet card surface -- a darker shade of the section
// background's accent so the cards "lift" off the soft -50/60 wash
// without pulling in white + grey-border noise. Borderless on
// purpose: the colour shift carries the lift.
const ACCENT_PAIN_CARD: Record<TradeContent["accentColour"], string> = {
  amber: "bg-amber-100/80 dark:bg-amber-900/30",
  blue: "bg-blue-100/80 dark:bg-blue-900/30",
  green: "bg-green-100/80 dark:bg-green-900/30",
  rose: "bg-rose-100/80 dark:bg-rose-900/30",
  violet: "bg-violet-100/80 dark:bg-violet-900/30",
  teal: "bg-teal-100/80 dark:bg-teal-900/30",
  orange: "bg-orange-100/80 dark:bg-orange-900/30",
};

// Per-trade decorative hero backdrop icon. Mirrors the per-trade
// chip vocabulary from _app.onboarding.tsx's TRADE_TYPES picker so
// a returning trader sees the same icon family they picked at sign-
// up. Renders giant + very low opacity behind the MockAppScreen
// (lg+ only) -- same treatment as the Quotes glyph on the
// "Sound familiar?" band.
const TRADE_HERO_ICON: Record<string, PhosphorIcon> = {
  plumbers: Wrench,
  electricians: Lightning,
  builders: HardHat,
  "gas-engineers": Flame,
  "hvac-engineers": Thermometer,
  cleaners: Broom,
  landscapers: Tree,
  painters: PaintBrush,
  locksmiths: LockKey,
  "hair-stylists": Scissors,
  "dog-groomers": Dog,
  tutors: Books,
  consultants: Briefcase,
  "all-trades": Storefront,
  "mobile-mechanics": Car,
};

export default function ForTrade() {
  const {
    trade,
    isProperty,
    promoPrice,
    painHero,
    tapToPayTicketDisplay,
    heroHeadline,
    heroSub,
    painPoints,
    benefits,
    workflow,
    mockAppScreen,
    faqs,
    displayPlural,
    otherTrades,
  } = useLoaderData<typeof loader>();
  const stats = trade.statsBand ?? DEFAULT_STATS;
  const checkClass = ACCENT_CHECK[trade.accentColour];
  const numeralClass = ACCENT_NUMERAL_BG[trade.accentColour];
  const headlineClass = ACCENT_HEADLINE[trade.accentColour];
  const painClass = ACCENT_PAIN[trade.accentColour];
  const painCardClass = ACCENT_PAIN_CARD[trade.accentColour];

  // Schema.org JSON-LD. Emits Service + Product + FAQPage in one
  // @graph so Google's structured-data parser can pick up rich results
  // (sitelinks, price, FAQ accordion) for the trade landing page.
  // Hand-rolled rather than pulling schema-dts -- the surface is small
  // and we keep the priority/changefreq decisions local.
  const ldJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://manos.team/#org",
        name: "Manos",
        url: "https://manos.team",
        // Was /logo.svg which 404'd. Use the canonical amber mark
        // (served via api/routes/GET-logo-mark.svg.ts). favicon.svg
        // is the rounded-tile variant -- not the right shape for
        // Google's logo box.
        logo: "https://manos.team/logo-mark.svg",
        sameAs: [],
      },
      {
        "@type": "Service",
        "@id": `https://manos.team/for/${trade.slug}#service`,
        name: `Manos for ${displayPlural}`,
        description:
          heroSub ?? `Software for ${displayPlural} -- jobs, clients, invoices.`,
        provider: { "@id": "https://manos.team/#org" },
        // areaServed as an array of the three markets we actually
        // serve. Was hardcoded "United Kingdom" before; geo-narrowing
        // per-visitor was the wrong fix because it would tell Google
        // we serve the visitor's country and only that. The array
        // is the canonical signal that one URL covers multiple
        // markets.
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "Country", name: "United States" },
          { "@type": "Country", name: "Canada" },
        ],
        serviceType: trade.title,
      },
      {
        // SoftwareApplication is a more specific subtype of Product
        // for SaaS. Eligible for the same rich-result formats and
        // adds applicationCategory + operatingSystem signals that
        // route the listing onto the right Google shelf.
        "@type": "SoftwareApplication",
        "@id": "https://manos.team/#product",
        name: "Manos",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "Jobs, clients, and invoices for sole traders, pairs, and small teams running every kind of service business.",
        brand: { "@id": "https://manos.team/#org" },
        image: "https://manos.team/og-image.png",
        offers: promoPrice
          ? {
              "@type": "Offer",
              price: promoPrice.amount,
              priceCurrency: promoPrice.currency,
              availability: "https://schema.org/InStock",
              url: `https://manos.team/for/${trade.slug}`,
            }
          : undefined,
      },
      ...(faqs && faqs.length > 0
        ? [
            {
              "@type": "FAQPage",
              "@id": `https://manos.team/for/${trade.slug}#faq`,
              mainEntity: faqs.map((f: { q: string; a: string }) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <script
        type="application/ld+json"
        // Stringified once; React serialises this as the script body.
        // Do NOT use a template literal with ${} interpolation outside
        // JSON.stringify -- a stray < or > in copy would break the
        // parser at the browser end.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />
      {/* Hero -- copy left, mock app screen right ----------------- */}
      <section className="py-20 relative overflow-hidden">
        <GradientHeroBackdrop accent={trade.accentColour} />
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-stone-100 dark:bg-stone-900 px-3 py-1 text-xs font-medium text-muted-foreground">
                Manos for {displayPlural}
              </div>
              {heroHeadline && (
                <h1 className="text-4xl md:text-5xl tracking-tight leading-tight">
                  <span className="font-bold text-foreground">
                    {heroHeadline.intro}
                  </span>
                  <br />
                  <span className={`font-extrabold ${headlineClass}`}>
                    {heroHeadline.punchline}
                  </span>
                </h1>
              )}
              {heroSub && (
                <p className="text-lg text-muted-foreground max-w-2xl lg:mx-0 mx-auto">
                  {heroSub}
                </p>
              )}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3 pt-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Link to="/sign-up" data-attr={`trade-${trade.slug}-hero-signup`}>Get started free</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/#features" data-attr={`trade-${trade.slug}-hero-features`}>See how it works</Link>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                3 days free, then {promoPrice} for 3 months. Cancel anytime.
              </p>
            </div>
            <div className="hidden lg:block relative">
              {/* Decorative trade-relative icon behind the mock
                  screen. Positioned inside the same grid cell so it
                  travels with the card on wide monitors instead of
                  drifting off to the right edge. Centred, scaled
                  larger than the card, very low opacity. */}
              {(() => {
                const Icon = TRADE_HERO_ICON[trade.slug];
                if (!Icon) return null;
                return (
                  <Icon
                    className={`absolute top-[75%] left-[120%] -translate-x-1/2 -translate-y-1/2 h-[32rem] w-[32rem] ${painClass} opacity-10 dark:opacity-[0.15] pointer-events-none`}
                    weight="fill"
                    aria-hidden="true"
                  />
                );
              })()}
              {mockAppScreen && (
                <MockAppScreen
                  title={mockAppScreen.title}
                  subtitle={mockAppScreen.subtitle}
                  iconKey={mockAppScreen.iconKey}
                  items={mockAppScreen.items}
                  accent={trade.accentColour}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats band ------------------------------------------------ */}
      <FadeInOnScroll>
        <StatsBand stats={stats} accent={trade.accentColour} />
      </FadeInOnScroll>

      {/* Sound familiar? -- own band with a soft trade-accent wash
          (lighter than the dark Cash/TapToPay bands below; just
          enough tint to feel distinct from the white sections above
          + below). Big decorative Quotes glyph as a backdrop pulls
          the empathy beat -- "we've heard this one before". The
          two cards inside (problem + solution) sit on top. */}
      <FadeInOnScroll>
        <section
          className={`py-24 relative overflow-hidden ${
            {
              amber: "bg-amber-50/60 dark:bg-amber-950/15",
              blue: "bg-blue-50/60 dark:bg-blue-950/15",
              green: "bg-green-50/60 dark:bg-green-950/15",
              rose: "bg-rose-50/60 dark:bg-rose-950/15",
              violet: "bg-violet-50/60 dark:bg-violet-950/15",
              teal: "bg-teal-50/60 dark:bg-teal-950/15",
              orange: "bg-orange-50/60 dark:bg-orange-950/15",
            }[trade.accentColour]
          }`}
        >
          <div className="relative mx-auto max-w-6xl px-6 space-y-10">
            {/* Decorative giant quote-mark, anchored to the inner
                content's coordinate system so it sits centred on
                where the text begins (not the section edge, which
                drifts off-screen on wide monitors). Lower-left of
                the content area, edge-offset like the hero icon. */}
            <Quotes
              className={`absolute top-[10%] left-0 -translate-x-1/2 -translate-y-1/2 h-80 w-80 ${painClass} opacity-[0.06] dark:opacity-[0.1] pointer-events-none`}
              weight="fill"
              aria-hidden="true"
            />
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
              Sound familiar?
            </h2>

            {/* Magazine-spread layout: editorial dialogue on the
                left, supporting pains stacked on the right. Reads as
                a main beat + "see also" sidebar without needing a
                visual divider between them -- the column gap does
                the work. Stacks to single-column on small screens
                (dialogue first, pains beneath). */}
            <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-10 lg:gap-16 items-start">
              {/* Left: stacked dialogue. Problem above, solution
                  below. Both left-aligned now (no need for the
                  L/R offset since the whole block sits in the
                  left column). */}
              {painHero && (
                <div className="space-y-6">
                  <p className="text-xl sm:text-2xl italic font-light leading-snug text-muted-foreground">
                    {painHero.problem}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold leading-snug">
                    {painHero.solutionPunchline ? (
                      <>
                        <span className="text-foreground">
                          {painHero.solution}
                        </span>
                        <br />
                        <span className={`font-extrabold ${painClass}`}>
                          {painHero.solutionPunchline}
                        </span>
                      </>
                    ) : (
                      <span className={`font-extrabold ${painClass}`}>
                        {painHero.solution}
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Right: stacked supporting pains. Each is a tinted
                  card with a Question icon. Vertical list at any
                  breakpoint -- no 3-col grid here. */}
              <ul className="space-y-3">
                {painPoints.map((point, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-3 rounded-md ${painCardClass} px-4 py-3 text-sm text-foreground/90`}
                  >
                    <Question
                      className={`h-5 w-5 ${painClass} shrink-0 mt-0.5`}
                      weight="fill"
                    />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Benefits -------------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20 bg-stone-50/50 dark:bg-secondary/20">
          <div className="mx-auto max-w-5xl px-6 space-y-10">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
              Built for the way you actually work
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {benefits.map((b, i) => (
                <Card key={i}>
                  <CardHeader>
                    <CardTitle className="flex items-start gap-2 text-base">
                      <CheckCircle
                        className={`h-5 w-5 ${checkClass} shrink-0 mt-0.5`}
                        weight="fill"
                      />
                      {b.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground">
                    {b.body}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </FadeInOnScroll>

      {/* Workflow -------------------------------------------------- */}
      {workflow && (
        <FadeInOnScroll>
          <section className="py-20">
            <div className="mx-auto max-w-3xl px-6 space-y-8">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
                {workflow.title}
              </h2>
              <ol className="space-y-4">
                {workflow.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span
                      className={`shrink-0 flex items-center justify-center h-8 w-8 rounded-full ${numeralClass} text-white text-sm font-semibold`}
                    >
                      {i + 1}
                    </span>
                    <p className="text-sm text-foreground pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </FadeInOnScroll>
      )}

      {/* Payment callouts -- both land on every trade page. Cash sits
          ABOVE tap-to-pay (paired pitches: 100%-yours anti-fee + phone
          IS the card reader).

          Each callout has its own FIXED brand colour, not the trade
          accent. Predictable across all trade pages, complementary to
          each other:
            - Cash:    emerald-green (semantic: money / cash)
            - TapToPay: Manos amber (brand colour)
            - HouseProud: blue (sub-brand, locked separately)
            - BookingPageHero: trade-accent (customer-facing surface)
          Trade-page chrome above the callouts (hero / mock / stats /
          familiar / benefits / workflow) carries the trade colour;
          these payment callouts anchor back to the brand-coherent
          three-colour band. */}
      <CashCallout
        accent="green"
        ticketDisplay={tapToPayTicketDisplay ?? undefined}
        imageSide="right"
      />
      <TapToPayCallout
        accent="amber"
        ticketDisplay={tapToPayTicketDisplay ?? undefined}
        ticketLabel={trade.tapToPayLabel}
        imageSide="left"
      />

      {/* HouseProud sub-brand hero (property trades) or BookingPage
          (non-property trades). Both show image-right by default --
          opposite of the TapToPay above so the page reads with a
          consistent left/right alternation. */}
      {isProperty && <HouseProudHero />}
      {!isProperty && (
        <BookingPageHero
          accent={trade.accentColour}
          slotLabel={trade.bookingHeroSlotLabel}
        />
      )}

      {/* FAQs ------------------------------------------------------ */}
      {faqs && faqs.length > 0 && (
        <FadeInOnScroll>
          <section className="py-20 bg-stone-50/50 dark:bg-secondary/20">
            <div className="mx-auto max-w-3xl px-6 space-y-8">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">
                Questions {displayPlural.toLowerCase()} ask
              </h2>
              <div className="space-y-4">
                {faqs.map((f, i) => (
                  <Card key={i}>
                    <CardHeader>
                      <CardTitle className="text-base">{f.q}</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      {f.a}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        </FadeInOnScroll>
      )}

      {/* CTA ------------------------------------------------------- */}
      <FadeInOnScroll>
        <section className="py-20">
          <div className="mx-auto max-w-2xl px-6 text-center space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              Spend the day on your customers, not your computer.
            </h2>
            <p className="text-sm text-muted-foreground">
              3 days free, then {promoPrice} for 3 months. No card charged until day 4.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Link to="/sign-up" data-attr={`trade-${trade.slug}-bottom-cta-signup`}>Get started free</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/#features" data-attr={`trade-${trade.slug}-bottom-cta-features`}>See how it works</Link>
              </Button>
            </div>
            <div className="pt-8 border-t border-border/50 mt-8">
              <p className="text-xs text-muted-foreground mb-3">
                Also using Manos:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {otherTrades.map((t) => (
                  <Link
                    key={t.slug}
                    to={`/for/${t.slug}`}
                    className="px-3 py-1 rounded-full text-xs bg-stone-100 dark:bg-stone-800 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                  >
                    {t.plural}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </FadeInOnScroll>
    </div>
  );
}

export function ErrorBoundary() {
  useReportRouteError("_public.for.$trade");
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center">
      <h1 className="text-2xl font-bold mb-2">Trade not found</h1>
      <p className="text-sm text-muted-foreground mb-6">
        No dedicated page for that one yet. Manos still works for it -- start
        from the homepage.
      </p>
      <Button asChild>
        <Link to="/">Back to homepage</Link>
      </Button>
    </div>
  );
}
